import { useState, Fragment } from "react";
import * as XLSX from "xlsx";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  generateNextItemId,
  createNewItem,
} from "../pages/projects/boqUploadService";
import { createMaterialStage } from "../pages/projects/projectMaterialService";
import { saveBoqItems } from "../pages/projects/projectBoqItemsService";

const functions = getFunctions(getApp(), "us-central1");
// Raised 170000→230000ms (13 Sep 2026) in lockstep with the server's
// timeoutSeconds (180→240) and CLAUDE_MAX_OUTPUT_TOKENS reduction
// (32000→18000) — see parseBoqWithAI.js for the measured-throughput math
// behind these numbers. Must always stay slightly under the server value.
const parseBoqWithAICallable = httpsCallable(functions, "parseBoqWithAI", {
  timeout: 230000, // 230 sec — just under the server's 240s timeoutSeconds
});

// Must stay in sync with backend's MAX_TOTAL_CHARS (functions/parseBoqWithAI.js).
// Dense BOQs (long spec paragraphs, e.g. chiller line items) can hit
// deadline-exceeded well under the 500-row cap — a 409-row/44K-char file
// failed 3 consecutive times before this fix. Splitting on whichever
// limit comes first (rows OR chars) is the real fix.
const MAX_CHARS_PER_CHUNK = 20000;

// Real-test evidence (Resort at Bilaspur file, re-test after the char-cap
// fix): a 161-row/~19,926-char chunk succeeded; a 205-row/~19,904-char
// chunk hit Claude's 16000-output-token cap (response truncated,
// stop_reason: "max_tokens") — despite nearly IDENTICAL character count.
// This shows ITEM COUNT drives output size, not input density: every row
// produces one full JSON object in Claude's response regardless of how
// short its source cells were. Capped well under the known-good 161 for
// safety margin, since other BOQs may produce more verbose item names.
const MAX_ROWS_PER_CHUNK = 140;

// Safety floor for adaptive splitting below — a single row this dense
// would mean one absurdly bloated cell; surface a real error here rather
// than looping forever trying to split further.
const MIN_ROWS_BEFORE_SPLIT = 10;

const parseFileToRows = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb   = XLSX.read(e.target.result, { type: "binary" });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        const nonBlank = rows.filter(r =>
          r.some(cell => String(cell || "").trim() !== "")
        );
        resolve(nonBlank);
      } catch (err) {
        reject(new Error("Could not read file"));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsBinaryString(file);
  });

// Splits a flat rows[] array into chunks, splitting at whichever limit
// comes first: `maxRows` rows OR `maxChars` total characters. Row-count
// alone doesn't predict Claude processing time — a chunk with long spec
// paragraphs (chiller line items) can be just as slow, or slower, than
// a bigger chunk of short line items.
const chunkRows = (rows, maxRows, maxChars) => {
  const chunks = [];
  let current = [];
  let currentChars = 0;

  for (const row of rows) {
    const rowChars = row.reduce((sum, cell) => sum + String(cell || "").length, 0);
    const wouldExceedRows = current.length >= maxRows;
    const wouldExceedChars = currentChars + rowChars > maxChars && current.length > 0;

    if (wouldExceedRows || wouldExceedChars) {
      chunks.push(current);
      current = [];
      currentChars = 0;
    }
    current.push(row);
    currentChars += rowChars;
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
};

// Words too generic to count as a meaningful match between a stage name
// and a checklist item name (e.g. matching on "unit" or "material" would
// give false-positive suggestions for almost every stage).
const STOPWORDS = new Set([
  "the", "and", "for", "of", "with", "unit", "units", "work", "works",
  "stage", "material", "materials", "side", "high", "low",
]);

const wordsOf = (s = "") =>
  s.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(
    (w) => w.length > 2 && !STOPWORDS.has(w)
  );

// DETERMINISTIC keyword-overlap suggestion — NOT an AI decision. Per
// Section 24's Business-Judgment-Stays-Human rule, checklist-trigger
// linking is a human call; this only pre-fills the MOST LIKELY option
// for the Manager to review/confirm/change, exactly like Item Master
// byName matching elsewhere in this same import flow.
const computeSuggestedTrigger = (stageName, items) => {
  if (!items?.length) return "";

  const stageWords = new Set(wordsOf(stageName));
  let best = null;
  let bestScore = 0;

  items.forEach((item) => {
    const itemWords = wordsOf(item.item_name);
    const overlap = itemWords.filter((w) => stageWords.has(w)).length;
    if (overlap > bestScore) {
      bestScore = overlap;
      best = item;
    }
  });
  if (best) return best.id;

  // Fallback — most HVAC checklist templates have one generic
  // "Material Requirement"-style milestone; if no stage-specific overlap
  // was found, suggest that generic item instead of leaving it blank.
  const generic = items.find((i) => /material/i.test(i.item_name));
  return generic ? generic.id : "";
};

// Even-split helper for the "Split across floors/zones" feature — same
// remainder-to-first-row convention already used by TransportLog.js's
// autoSplit(), for consistency across the app. Purely a starting-point
// default; the Manager can freely edit every allocation before applying.
const initSplitAllocations = (totalQty, count) => {
  const per = Math.round((totalQty / count) * 100) / 100;
  const roundedTotal = Math.round(per * count * 100) / 100;
  const remainder = Math.round((totalQty - roundedTotal) * 100) / 100;
  return Array.from({ length: count }, (_, i) => ({
    label: `Floor ${i + 1}`,
    qty: i === 0 ? per + remainder : per,
  }));
};

// Deterministic keyword guess — NOT an AI decision (Section 24). Per D-195,
// serialized HVAC equipment (Outdoor Units, Chillers, AHUs, VRF units, etc.)
// is deliberately kept OUT of Item Master and tracked separately via
// Equipment Registry. Without this check, AI BOQ Import would silently
// create Item Master entries for equipment too (since it just sees "row
// with no Item Master match" and defaults to "new Material item") — this
// pre-classifies rows so the Manager reviews/confirms the split on the
// Preview screen, same as every other AI-suggested field in this panel.
const EQUIPMENT_KEYWORDS = [
  "airconditioner", "air conditioner", "outdoor unit", "indoor unit",
  "vrf", "vrv", "chiller", "ahu", "fcu", "condensing unit", "cassette",
  "ductable", "split unit", "package unit", "cooling tower", "compressor",
  "heat pump", "air handling unit",
];

// Deterministic keyword guess — NOT an AI decision (Section 24). Labor/
// service line items ("Chiseling Work on Walls and Floor", "Core Cutting
// Charge 100mm dia", "Scaffolding Charge") were previously defaulting to
// Material and silently polluting Item Master — client-flagged (D-256).
// Routes to Subcontractor Work/DPR instead, same pattern as Equipment
// routes to Equipment Registry. First-pass list from the client's own
// examples — expected to be refined after real-file testing, same
// iterative convention already used for EQUIPMENT_KEYWORDS.
const SERVICE_LABOR_KEYWORDS = [
  "installation", "testing", "commissioning", "chiseling", "core cutting",
  "scaffolding", "labour charge", "labor charge", "charges", "erection",
  "fabrication charge", "civil work", "painting", "welding charge",
  "supervision", "mobilization", "demobilization", "transportation",
];

// PRIORITY ORDER FIXED (13 Sep 2026): Service/Labor keywords now checked
// BEFORE Equipment keywords. Real HVAC BOQ line items for installation/
// transportation/commissioning work almost always mention the equipment
// name too (e.g. "Chiller Transportation & Installation", "AHU Room
// Thermal/Acoustic Wall & Ceiling") — with Equipment checked first, EVERY
// such labor/service line was misclassified as Equipment purely because
// it happened to mention "chiller"/"ahu"/etc., and the Service/Labor
// bucket silently caught nothing across an entire real BOQ. An action
// word (installation, transportation, commissioning...) is a stronger,
// more specific signal that a row is labor/service than an equipment
// name appearing anywhere in the same string — so it must win the tie.
// "transportation" added to the keyword list below since it recurred in
// this session's real test file and wasn't previously covered.
// Extracted (13 Sep fix) so the matched-item bypass fix below can reuse
// the exact same Service/Labor keyword check guessRowType() uses —
// single source of truth for this keyword list, no duplicated logic.
const hasServiceLaborKeyword = (itemName = "") => {
  const lower = itemName.toLowerCase();
  return SERVICE_LABOR_KEYWORDS.some((k) => lower.includes(k));
};

const guessRowType = (itemName = "") => {
  if (hasServiceLaborKeyword(itemName)) return "service";
  const lower = itemName.toLowerCase();
  if (EQUIPMENT_KEYWORDS.some((k) => lower.includes(k))) return "equipment";
  return "material";
};

// Canonical real-world HVAC work sequence — confirmed with the user.
// Stage-creation order (and therefore stage_no, via the D-202 atomic
// counter inside createMaterialStage()) should follow actual construction
// sequence, NOT whatever row-order the source Excel/AI extraction happened
// to produce. A stage name matches the FIRST group whose keyword it contains.
const STAGE_SEQUENCE_KEYWORDS = [
  ["civil"],
  ["piping"],
  ["electrical"],
  ["duct", "ventilation"],
  ["indoor"],
  ["outdoor"],
  ["testing", "commissioning"],
];

// Unmatched/unrecognized stage names are placed AFTER all recognized
// stages (safest default — never jumps ahead of known early-sequence
// work like Civil/Piping).
const getStagePriority = (stageName = "") => {
  const lower = stageName.toLowerCase();
  const idx = STAGE_SEQUENCE_KEYWORDS.findIndex((group) =>
    group.some((kw) => lower.includes(kw))
  );
  return idx === -1 ? STAGE_SEQUENCE_KEYWORDS.length : idx;
};

export default function AIBoqImportPanel({
  project_id,
  project_name,
  user,
  checklistItems = [],
  onClose,
  onDone,
}) {
  const company_id = user?.company_id || "";

  const [step,      setStep]      = useState("upload"); // upload | loading | preview | saving
  const [error,     setError]     = useState("");
  const [warnings,  setWarnings]  = useState([]);
  const [usage,     setUsage]     = useState(null);
  const [rows,      setRows]      = useState([]); // extracted rows with edit state
  const [progress,  setProgress]  = useState(null); // { current, total } — shown during multi-chunk processing
  // Generalized (13 Sep 2026, final architecture fix): previously only
  // set for quota exhaustion. Now covers ANY chunk failure that couldn't
  // be self-healed via split-retry — once even one chunk has succeeded,
  // the import degrades gracefully (skip that one part, keep going)
  // instead of silently losing data or aborting the whole thing. This is
  // the root fix for the "why did it only show partial rows with no
  // explanation" class of failure — every future unrecognized error now
  // becomes a VISIBLE, labeled skip instead of a silent gap or a scary
  // abort. isQuotaExhausted is the one reason that also needs to stop the
  // WHOLE queue early (quota blocks every subsequent call); every other
  // reason just skips that one part.
  const [partialStop, setPartialStop] = useState(null); // { processedChunks, totalChunks, isQuotaExhausted }

  // Stage → checklist-item unlock mapping. Keyed by trimmed stage name.
  // A suggested match is pre-filled deterministically (keyword lookup —
  // NEVER an AI guess, per Section 24's business-judgment rule) but the
  // Manager must review/confirm it here before "Confirm & Create" — same
  // mandatory-human-review discipline as every other field in this panel.
  const [stageTriggerMap, setStageTriggerMap] = useState({}); // { [stageName]: checklistItemId }

  // ── SPLIT ACROSS FLOORS/ZONES ────────────────────────────────────────
  // Real HVAC projects often run the same BOQ line (e.g. "Piping - Low
  // Side, 190 Rmt") across multiple floors/zones simultaneously — the AI
  // extracts one pooled total per row, since that's genuinely what the
  // source Excel contains. Splitting that pooled quantity into per-floor
  // portions is a business decision (how much goes where), so it stays a
  // manual, reviewed step here — never auto-decided — matching this
  // panel's existing "Side" and "checklist trigger" conventions.
  const [splittingKey, setSplittingKey] = useState(null); // _key of the row currently being split, or null
  const [splitAllocations, setSplitAllocations] = useState([]); // [{ label, qty }]

  const openSplit = (row) => {
    setSplittingKey(row._key);
    setSplitAllocations(initSplitAllocations(row.qty, 2));
  };

  const closeSplit = () => {
    setSplittingKey(null);
    setSplitAllocations([]);
  };

  const updateSplitAllocation = (index, field, value) => {
    setSplitAllocations((allocs) =>
      allocs.map((a, i) => (i === index ? { ...a, [field]: value } : a))
    );
  };

  const addSplitAllocation = () => {
    setSplitAllocations((allocs) => [
      ...allocs,
      { label: `Floor ${allocs.length + 1}`, qty: "" },
    ]);
  };

  const removeSplitAllocation = (index) => {
    setSplitAllocations((allocs) => allocs.filter((_, i) => i !== index));
  };

  const applySplit = () => {
    const row = rows.find((r) => r._key === splittingKey);
    if (!row) { closeSplit(); return; }

    const validAllocs = splitAllocations.filter((a) => a.label.trim());
    const allocatedTotal = Math.round(
      validAllocs.reduce((s, a) => s + Number(a.qty || 0), 0) * 100
    ) / 100;
    const originalQty = Math.round(Number(row.qty || 0) * 100) / 100;

    if (validAllocs.length < 2) {
      alert("Add at least 2 locations to split across.");
      return;
    }
    if (Math.abs(allocatedTotal - originalQty) > 0.01) {
      alert(
        `Split total (${allocatedTotal}) doesn't match this row's original quantity (${originalQty}). Please adjust the amounts.`
      );
      return;
    }

    const newRows = validAllocs.map((a, i) => ({
      ...row,
      _key: `${row._key}-split${i}-${Date.now()}`,
      stage: `${a.label.trim()} — ${row.stage}`,
      qty: Number(a.qty),
    }));

    setRows((rs) => {
      const idx = rs.findIndex((r) => r._key === row._key);
      const updated = [...rs];
      updated.splice(idx, 1, ...newRows);
      return updated;
    });
    closeSplit();
  };

  // ── FILE UPLOAD → CLOUD FUNCTION (chunked if large) ─────────────────────
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setError("");
    setPartialStop(null);
    setStep("loading");

    try {
      const allRows = await parseFileToRows(file);
      const chunks = chunkRows(allRows, MAX_ROWS_PER_CHUNK, MAX_CHARS_PER_CHUNK);

      // Large file → confirm before consuming multiple quota units
      if (chunks.length > 1) {
        const proceed = window.confirm(
          `This BOQ has ${allRows.length} rows and is too large for a single AI import.\n\n` +
          `It will be processed in at least ${chunks.length} parts (more if any part needs ` +
          `further splitting due to content density), using at least ${chunks.length} of your ` +
          `monthly AI import quota (instead of 1).\n\n` +
          `Continue?`
        );
        if (!proceed) {
          setStep("upload");
          return;
        }
      }

      // ── ADAPTIVE QUEUE ──────────────────────────────────────────────
      // Starts from the density-based chunks above, but a fixed row/char
      // heuristic can NEVER fully guarantee Claude's output won't hit its
      // own token cap — real testing proved this: two chunks of the exact
      // same row count (140) produced 14,127 vs 16,000+ output tokens
      // depending purely on content composition, not input size. Rather
      // than chase an ever-smaller static threshold, this self-corrects:
      // if a chunk genuinely truncates, it's split in half and re-queued
      // — this makes the import capable of any BOQ's density without
      // needing to predict it in advance.
      const queue = [...chunks];
      let mergedItems = [];
      let mergedWarnings = [];
      let lastUsage = null;
      let keyCounter = 0;
      let processedChunks = 0;
      let callsMade = 0;

      while (queue.length > 0) {
        const currentChunk = queue.shift();
        callsMade++;
        setProgress({ current: callsMade, total: Math.max(chunks.length, callsMade + queue.length) });

        try {
          const result = await parseBoqWithAICallable({
            rows:       currentChunk,
            project_id,
            company_id, // sanity-check only — server uses auth token's company_id
          });

          const data  = result.data || {};
          const items = Array.isArray(data.items) ? data.items : [];

          mergedItems = mergedItems.concat(
            items.map((it) => ({
              _key:              keyCounter++,
              include:           true,
              stage:             it.stage || "",
              item_name:         it.item_name || "",
              // description/brand_make/section_path — carried through
              // purely for the "Full Scope" reference save
              // (projectBoqItemsService.js, Task 4.5). Never used in
              // Item Master matching or stage/budget logic — those stay
              // keyed on item_name only, per Task 4's confirmed decision.
              description:       it.description  || "",
              brand_make:        it.brand_make   || "",
              section_path:      Array.isArray(it.section_path) ? it.section_path : [],
              unit:              it.unit || "Nos",
              qty:               Number(it.qty || 0),
              rate:              Number(it.rate || 0),
              side:              "high_side", // AI never classifies this — human default
              matched_item_id:   it.matched_item_id   || "",
              matched_item_name: it.matched_item_name || "",
              is_new_item:       !it.matched_item_id,
              // FIXED — matched-item bypass gap: a row can match an
              // EXISTING Item Master entry that was itself mis-classified
              // before the D-256 Service/Labor bucket existed (e.g. an
              // old "Scaffolding Charge" entry wrongly saved as
              // Material). Re-check ONLY the Service/Labor keywords for
              // matched rows — never Equipment, since equipment was
              // never permitted into Item Master in the first place
              // (D-195), so a matched row can never genuinely BE
              // equipment; guessing "equipment" here would only be a
              // false positive from an equipment-sounding word inside a
              // legitimately-matched material's own name.
              type: it.matched_item_id
                ? (hasServiceLaborKeyword(it.item_name || "") ? "service" : "material")
                : guessRowType(it.item_name || ""),
            }))
          );

          const chunkWarnings = Array.isArray(data.warnings) ? data.warnings : [];
          if (chunkWarnings.length > 0) {
            mergedWarnings = mergedWarnings.concat(chunkWarnings.map(w => `[Part ${callsMade}] ${w}`));
          }

          lastUsage = data.usage || null;
          processedChunks++;
        } catch (chunkErr) {
          // ── STEP 1: SELF-HEAL VIA SPLIT-RETRY ──────────────────────────
          // Tried FIRST, whenever the failure looks like "this chunk was
          // too much for one Claude call" — regardless of which specific
          // transport symptom carried that signal:
          //   - "out-of-range": server had time to detect genuine
          //     truncation (via Claude's own stop_reason — see
          //     parseBoqWithAI.js) and classify it cleanly before its own
          //     timeout.
          //   - "deadline-exceeded": the call ran out of time before
          //     Claude could even finish generating (confirmed via GCP
          //     Logs Explorer, 13 Sep 2026 — measured throughput
          //     ~135-145 tokens/sec, some chunks needed longer than the
          //     timeout budget to finish).
          // Both are symptomatically identical in practice and split
          // identically — this is unchanged from the earlier fix.
          const isTruncated = chunkErr?.code === "functions/out-of-range";
          const isTimeout    = chunkErr?.code === "functions/deadline-exceeded";
          if ((isTruncated || isTimeout) && currentChunk.length > MIN_ROWS_BEFORE_SPLIT) {
            const mid = Math.ceil(currentChunk.length / 2);
            // ORDER BUG FIXED (13 Sep 2026): queue.unshift(A, B) inserts as
            // [A, B, ...rest] — so this MUST be (firstHalf, secondHalf) to
            // preserve original source-file row sequence. The previous
            // (secondHalf, firstHalf) argument order silently REVERSED
            // this chunk's rows every time a split happened. Since Full
            // Scope's row_order field (projectBoqItemsService.js) is
            // derived directly from final processing order, this
            // corrupted the exact source-sequence guarantee that IS the
            // point of that feature — never caught until traced
            // end-to-end against a real multi-split file.
            queue.unshift(currentChunk.slice(0, mid), currentChunk.slice(mid));
            callsMade--; // this attempt produced no usable output — don't count it in the visible part number
            continue;
          }

          // ── STEP 2: CLASSIFY THE FAILURE FOR REPORTING ─────────────────
          const isQuotaExhausted =
            chunkErr?.code === "functions/resource-exhausted" ||
            /resource-exhausted|monthly.*limit/i.test(chunkErr?.message || "");

          // ── STEP 3: GRACEFUL PARTIAL STOP — THE ARCHITECTURAL FIX ──────
          // Historically, anything that didn't match a known retryable
          // code fell through to `throw chunkErr`, aborting the ENTIRE
          // import and discarding every item already extracted from
          // earlier successful chunks — often with no visibility into WHY
          // beyond a raw Firebase error string, or (worse) with the loop
          // exiting via `break` in a way that left NO trace in the UI at
          // all. Real testing proved this isn't hypothetical: a
          // truncation whose specific JSON parse-error text didn't match
          // the old detection regex fell through exactly this way and
          // silently killed an otherwise-recoverable import.
          //
          // NEW RULE: once ANYTHING has been salvaged (processedChunks >
          // 0), NO single chunk failure — known or never-before-seen —
          // is allowed to silently lose data or scare the user with a
          // dead end. That one part is skipped, clearly logged as a
          // warning the Manager will see in Preview, and the loop keeps
          // going (except for quota exhaustion, which genuinely blocks
          // every subsequent call — no point continuing to try).
          if (processedChunks > 0) {
            const reasonText = isQuotaExhausted
              ? "Monthly AI import quota reached"
              : `${chunkErr?.code || "error"}: ${chunkErr?.message || "Unknown error"}`;
            mergedWarnings.push(
              `[Part ${callsMade}] ⚠ This part could not be processed (${reasonText}) — its rows were skipped.` +
              (isQuotaExhausted
                ? " Import the remaining rows next month, or ask support to raise your limit."
                : " You can try re-uploading just this portion of the file separately.")
            );
            setPartialStop({
              processedChunks,
              totalChunks: callsMade + queue.length,
              isQuotaExhausted,
            });
            if (isQuotaExhausted) break; // blocks every remaining call — stop the queue entirely
            continue; // any other failure — only THIS part is lost, the rest of the queue can still succeed
          }

          // ── STEP 4: NOTHING SALVAGED YET — genuine full-stop ───────────
          // Only reached when the very FIRST attempted chunk fails (there
          // is nothing to show, so a real, friendly error is appropriate).
          if (isQuotaExhausted) {
            throw new Error(
              `Monthly AI import quota was already reached before any part of this file could be processed. ${chunkErr?.message || ""}`
            );
          }
          if (isTimeout) {
            throw new Error(
              "Even the smallest attempted portion of this file is taking too long to process. Please split this sheet manually into smaller sections and try again, or contact support if this keeps happening."
            );
          }
          if (isTruncated) {
            throw new Error(
              "Even the smallest attempted portion of this file was too content-dense for a single AI import. Please split this sheet manually into smaller sections and try again."
            );
          }
          throw chunkErr;
        }
      }

      setProgress(null);

      if (mergedItems.length === 0) {
        throw new Error("No usable line items found in this file. Please check the file and try again.");
      }

      setRows(mergedItems);
      setWarnings(mergedWarnings);
      setUsage(lastUsage);

      // Pre-fill a suggested checklist-unlock trigger per unique stage —
      // deterministic keyword match only, always editable, never auto-applied
      // without the Manager reviewing it on this same screen before Confirm.
      const uniqueStages = [...new Set(mergedItems.map((r) => r.stage.trim()).filter(Boolean))];
      const suggestedMap = {};
      uniqueStages.forEach((stageName) => {
        suggestedMap[stageName] = computeSuggestedTrigger(stageName, checklistItems);
      });
      setStageTriggerMap(suggestedMap);

      setStep("preview");
    } catch (err) {
      console.error("AI BOQ parse error:", err);
      setProgress(null);
      setError(
        err?.message ||
        "AI import failed. If this company doesn't have AI BOQ Import enabled, contact SuperAdmin."
      );
      setStep("upload");
    }
  };

  // ── ROW EDIT HELPERS ─────────────────────────────────────────────────────
  const updateRow = (key, field, value) => {
    setRows(rs => rs.map(r => (r._key === key ? { ...r, [field]: value } : r)));
  };

  const updateStageTrigger = (stageName, checklistItemId) => {
    setStageTriggerMap((m) => ({ ...m, [stageName]: checklistItemId }));
  };

  // Recomputes which unique stage names currently exist in `rows` — a row's
  // stage name can be hand-edited in the table below (or split across
  // floors/zones), so this stays in sync rather than being frozen at the
  // moment of AI extraction.
  const uniqueStageNames = [...new Set(
    rows.filter((r) => r.type === "material").map((r) => r.stage.trim()).filter(Boolean)
  )];

  // ── CONFIRM → CREATE ITEMS + STAGES (existing governed functions only) ──
  const handleConfirm = async () => {
    setError("");
    setStep("saving");
    try {
      const included = rows.filter(r => r.include && r.stage.trim() && r.item_name.trim());
      if (included.length === 0) {
        throw new Error("No rows selected to import.");
      }

      // Equipment and Service/Labor rows NEVER touch Item Master or a
      // stage's material_budget. Equipment (D-195) is tracked separately
      // via Equipment Registry; Service/Labor (D-256 — "Chiseling Work",
      // "Core Cutting Charge", "Scaffolding Charge" etc. were silently
      // polluting Item Master as Material) routes to Subcontractor
      // Work/DPR instead. Only their names are kept, purely to tell the
      // Manager afterward what was skipped and needs manual entry there.
      const materialRows  = included.filter(r => r.type === "material");
      const equipmentRows = included.filter(r => r.type === "equipment");
      const serviceRows   = included.filter(r => r.type === "service");

      // "Full Scope" reference save (Task 4.5) — the ENTIRE original BOQ
      // as specified, equipment included. This is intentionally separate
      // from and unrelated to material_budget/Item Master — D-195's
      // equipment exclusion applies only to the procurement/stock domain
      // (project_material_stages), not to this read-only reference view.
      // Non-blocking by design: a Full Scope save hiccup must never
      // prevent the real stage-creation write below from succeeding —
      // same convention as notifyMaterialStageUnlocked() elsewhere in
      // this app (projectMaterialService.js).
      let boqItemsSaved = 0;
      let boqSaveError  = null;
      try {
        const { itemsSaved } = await saveBoqItems(company_id, project_id, project_name, included, user);
        boqItemsSaved = itemsSaved;
      } catch (err) {
        // Partial progress (if any earlier batch DID commit) is attached
        // by saveBoqItems() itself via err.itemsSaved — read it here so a
        // mid-batch failure is never silently invisible to the Manager.
        // Stage creation below still proceeds regardless — Full Scope is
        // a reference view, never a blocker for the real procurement write.
        boqItemsSaved = Number(err?.itemsSaved || 0);
        boqSaveError  = err?.message || "Unknown error";
        console.warn("saveBoqItems failed (partial — surfaced in summary alert below):", err);
      }


      const newItemNames = []; // tracked ONLY so the post-save summary can name exactly what was silently created — nothing here is a business decision, just a visibility log for the Manager.

      // Resolve/create item_id for MATERIAL rows only
      for (const row of materialRows) {
        if (row.matched_item_id) continue;
        // D-300: one reserved id per new item (never "+1" locally — another
        // company may be reserving at the same moment).
        const item_id = await generateNextItemId();
        const created = await createNewItem(company_id, item_id, row.item_name, row.unit, row.description);
        row.matched_item_id   = created.item_id;
        row.matched_item_name = created.item_name;
        newItemNames.push(created.item_name);
      }

      // Group MATERIAL rows by stage name — this naturally merges
      // same-named stages across chunks too, since `materialRows` is a
      // single flat array by now. Equipment rows never reach this point —
      // they don't get a stage, a budget entry, or an Item Master entry;
      // they're only listed in the post-save summary below so nothing
      // silently disappears.
      const stageGroups = {};
      materialRows.forEach(row => {
        const key = row.stage.trim();
        if (!stageGroups[key]) stageGroups[key] = [];
        stageGroups[key].push(row);
      });

      // Create stages in real-world construction sequence (Civil → Piping →
      // Electrical → Ductwork → Indoor Units → Outdoor Units → Testing/
      // Commissioning), NOT the raw source-file row order — this also
      // determines stage_no via createMaterialStage()'s atomic counter.
      const orderedStageEntries = Object.entries(stageGroups).sort(
        (a, b) => getStagePriority(a[0]) - getStagePriority(b[0])
      );

      let stagesCreated = 0;

      for (const [stageName, groupRows] of orderedStageEntries) {
        const material_budget = groupRows.map(row => {
          const buffer_pct = row.side === "low_side" ? 15 : 0;
          const qty_final  = row.side === "low_side"
            ? Math.ceil(row.qty * (1 + buffer_pct / 100))
            : row.qty;
          return {
            item_id:       row.matched_item_id,
            item_code:     "",
            item_name:     row.matched_item_name || row.item_name,
            unit:          row.unit,
            side:          row.side,
            buffer_pct,
            qty_entered:   row.qty,
            qty_budgeted:  qty_final,
            qty_issued:    0,
            qty_remaining: qty_final,
            cost_rate:     row.rate,
            budget_amount: qty_final * row.rate,
            issued_amount: 0,
            alert_level:   "ok",
            budget_pct:    0,
            override_log:  [],
          };
        });

        const chosenTriggerId = stageTriggerMap[stageName];
        await createMaterialStage({
          project_id,
          project_name,
          stage_name:       stageName,
          // Pre-filled suggestion, reviewed/confirmed by the Manager on the
          // Preview screen above — NOT an AI decision (Section 24). Empty
          // if the Manager left it unselected — same "stays Locked until
          // manually linked" behavior as before this change.
          trigger_item_ids: chosenTriggerId ? [chosenTriggerId] : [],
          material_budget,
        }, user);

        stagesCreated++;
      }

      // Full transparency summary — this directly replaces the old
      // "N stage(s) created" one-liner that gave no visibility into what
      // actually got written to Item Master. The Manager sees exactly
      // what was newly created and what was deliberately skipped, right
      // when it happens — not days later when browsing Item Master.
      let summary = `✅ AI BOQ Import complete!\n${stagesCreated} stage(s) created.`;

      if (boqItemsSaved > 0) {
        summary += `\n\n📖 Full Scope reference saved (${boqItemsSaved} item${boqItemsSaved === 1 ? "" : "s"}) — view the complete original BOQ, including equipment, brand/make, and full specs, under the "Full Scope" tab.`;
      }

      if (boqSaveError) {
        summary += `\n\n⚠️ Full Scope reference did NOT finish saving (${boqItemsSaved} of ${included.length} items saved) — error: ${boqSaveError}. The Material Stage(s) above were still created successfully and are unaffected. Full Scope is append-only, so re-running AI BOQ Import later is safe and will add the remaining reference items.`;
      }

      if (newItemNames.length > 0) {
        summary += `\n\n🆕 New items added to Item Master (${newItemNames.length}):\n`
          + newItemNames.map(n => `• ${n}`).join("\n");
      }

     if (equipmentRows.length > 0) {
        summary += `\n\n⛔ Equipment items — NOT added to Item Master or any stage (${equipmentRows.length}):\n`
          + equipmentRows.map(r => `• ${r.item_name} (${r.qty} ${r.unit})`).join("\n")
          + `\n\nAdd these manually via Tools & Assets → Equipment Registry.`;
      }

      if (serviceRows.length > 0) {
        summary += `\n\n🔨 Service/Labor items — NOT added to Item Master or any stage (${serviceRows.length}):\n`
          + serviceRows.map(r => `• ${r.item_name} (${r.qty} ${r.unit})`).join("\n")
          + `\n\nAdd these manually via Subcontractor Work or DPR.`;
      }

      alert(summary);
      onDone && onDone();
    } catch (err) {
      console.error("AI BOQ save error:", err);
      setError(err.message || "Save failed");
      setStep("preview");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">

        <div className="flex justify-between items-center p-5 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-800">🤖 AI Smart BOQ Import</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Upload any BOQ Excel — AI extracts and groups it. You review before anything is saved.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>

        <div className="p-5">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
              {error}
            </div>
          )}

          {step === "upload" && (
            <div className="text-center py-10">
              <div className="text-5xl mb-3">📄</div>
              <p className="text-sm text-gray-500 mb-4">
                Select the client's BOQ file (any format/headers, any length).
              </p>
              <label className="px-5 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-semibold cursor-pointer hover:bg-purple-700">
                Choose File
                <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} />
              </label>
            </div>
          )}

          {step === "loading" && (
            <div className="text-center py-16 text-gray-400">
              <div className="text-4xl mb-3 animate-pulse">🤖</div>
              <p className="font-semibold">
                {progress
                  ? `Reading and extracting BOQ — part ${progress.current} of ${progress.total}...`
                  : "Reading and extracting BOQ..."}
              </p>
              <p className="text-xs mt-1">This can take up to a couple of minutes per part
         for large files.</p>
            </div>
          )}

          {(step === "preview" || step === "saving") && (
            <>
              {partialStop && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-xl text-xs text-orange-700">
                  {partialStop.isQuotaExhausted ? (
                    <>⚠ Monthly AI import quota reached after processing {partialStop.processedChunks} of{" "}
                    {partialStop.totalChunks} parts of this file. The items below are from the parts that
                    completed — you can confirm these now, and import the remaining rows next month
                    (or ask support to raise your limit).</>
                  ) : (
                    <>⚠ {partialStop.processedChunks} of {partialStop.totalChunks} parts of this file were
                    processed. One or more parts could not be processed and were skipped — see the warnings
                    below for exactly which. You can confirm the items below now, and try re-uploading just
                    the skipped part(s) separately.</>
                  )}
                </div>
              )}

              {warnings.length > 0 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700 space-y-1">
                  {warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}
              {usage && (
                <p className="text-xs text-gray-400 mb-3">
                  AI usage this month: {usage.used ?? "—"}/{usage.cap ?? "—"}
                </p>
              )}

              {/* STAGE → CHECKLIST UNLOCK TRIGGER — suggested, always editable */}
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-xl text-xs text-orange-700">
                📦/🖥️/🔨 Every row is guessed as <b>Material</b>, <b>Equipment</b>, or <b>Service/Labor</b> below
                (editable per row). Material rows get added to <b>Item Master</b> if no existing match is found.
                Equipment rows (AC units, outdoor units, chillers, etc.) and Service/Labor rows (installation,
                testing &amp; commissioning, chiseling, scaffolding, etc.) are <b>never</b> added to Item Master —
                Equipment should be added via Tools &amp; Assets → Equipment Registry, and Service/Labor via
                Subcontractor Work or DPR, per this app's standing rule keeping both out of the material catalog.
              </div>

              {uniqueStageNames.length > 0 && (
                <div className="mb-4 border border-blue-100 bg-blue-50/40 rounded-xl p-3">
                  <p className="text-xs font-semibold text-gray-600 mb-2">
                    🔗 Unlock trigger per stage
                    <span className="font-normal text-gray-400 ml-1">
                      — suggested automatically, review and change if needed. Leave blank to link manually later.
                    </span>
                  </p>
                  {checklistItems.length === 0 ? (
                    <p className="text-xs text-amber-600">
                      No checklist items found for this project yet — stages will be created Locked with no trigger. You can link them later via each stage's ✏️ Edit Triggers.
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {uniqueStageNames.map((stageName) => (
                        <div key={stageName} className="flex items-center gap-2 text-xs">
                          <span className="w-40 shrink-0 font-medium text-gray-700 truncate">{stageName}</span>
                          <span className="text-gray-300">→</span>
                          <select
                            value={stageTriggerMap[stageName] || ""}
                            onChange={(e) => updateStageTrigger(stageName, e.target.value)}
                            className="flex-1 border rounded-lg px-2 py-1 text-xs outline-none bg-white"
                          >
                            <option value="">— No trigger (stays Locked) —</option>
                            {checklistItems.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.item_no ? `#${item.item_no} ` : ""}{item.item_name}
                              </option>
                            ))}
                          </select>
                          {stageTriggerMap[stageName] && (
                            <span className="text-green-600 shrink-0">✓ will link</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="overflow-x-auto border rounded-xl">
                <table className="w-full min-w-[1000px] text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2"></th>
                      <th className="p-2 text-left">Stage</th>
                      <th className="p-2 text-left">Item Name</th>
                      <th className="p-2 text-center">Unit</th>
                      <th className="p-2 text-right">Qty</th>
                      <th className="p-2 text-right">Rate</th>
                      <th className="p-2 text-center">Side</th>
                      <th className="p-2 text-center">Type</th>
                      <th className="p-2 text-center">Match</th>
                      <th className="p-2 text-center">Split</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map(row => (
                      <Fragment key={row._key}>
                        <tr className={!row.include ? "opacity-40" : ""}>
                          <td className="p-2 text-center">
                            <input type="checkbox" checked={row.include}
                              onChange={e => updateRow(row._key, "include", e.target.checked)} />
                          </td>
                          <td className="p-2">
                             <input value={row.stage}
                              onChange={e => updateRow(row._key, "stage", e.target.value)}
                              className="border rounded px-1 py-0.5 w-36 text-xs" />
                          </td>
                          <td className="p-2 min-w-[260px]">
                            <input value={row.item_name}
                              onChange={e => updateRow(row._key, "item_name", e.target.value)}
                              className="border rounded px-1 py-0.5 w-full text-xs" />
                          </td>
                          <td className="p-2 text-center">
                            <input value={row.unit}
                              onChange={e => updateRow(row._key, "unit", e.target.value)}
                              className="border rounded px-1 py-0.5 w-14 text-xs text-center" />
                          </td>
                          <td className="p-2 text-right">
                            <input type="number" value={row.qty}
                              onChange={e => updateRow(row._key, "qty", Number(e.target.value))}
                              className="border rounded px-1 py-0.5 w-16 text-xs text-right" />
                          </td>
                          <td className="p-2 text-right">
                            <input type="number" value={row.rate}
                              onChange={e => updateRow(row._key, "rate", Number(e.target.value))}
                              className="border rounded px-1 py-0.5 w-16 text-xs text-right" />
                          </td>
                          <td className="p-2 text-center">
                            <select value={row.side}
                              onChange={e => updateRow(row._key, "side", e.target.value)}
                              className="border rounded px-1 py-0.5 text-xs">
                              <option value="high_side">🏗️ High</option>
                              <option value="low_side">🔧 Low</option>
                            </select>
                          </td>
                           <td className="p-2 text-center">
                            <select value={row.type}
                              onChange={e => updateRow(row._key, "type", e.target.value)}
                              title="Equipment and Service/Labor are never added to Item Master — track them via Equipment Registry / Subcontractor Work-DPR instead"
                              className={`border rounded px-1 py-0.5 text-xs font-semibold ${
                                row.type === "equipment" ? "bg-orange-50 text-orange-700 border-orange-200" :
                                row.type === "service"   ? "bg-purple-50 text-purple-700 border-purple-200" : ""
                              }`}>
                              <option value="material">📦 Material</option>
                              <option value="equipment">🖥️ Equipment</option>
                              <option value="service">🔨 Service/Labor</option>
                            </select>
                          </td>
                          <td className="p-2 text-center">
                            {row.type === "equipment"
                              ? <span className="text-orange-600 font-semibold">⛔ Skip Item Master</span>
                              : row.type === "service"
                              ? <span className="text-purple-600 font-semibold">🔨 Skip Item Master</span>
                              : row.matched_item_id
                              ? <span className="text-green-600 font-semibold">✅ Matched</span>
                              : <span className="text-blue-600 font-semibold">🆕 New</span>}
                          </td>
                          <td className="p-2 text-center">
                            <button type="button" onClick={() => openSplit(row)}
                              title="Split this quantity across multiple floors/zones"
                              className="text-purple-600 hover:text-purple-800 font-semibold">
                              ✂️ Split
                            </button>
                          </td>
                        </tr>

                        {/* Inline split panel — only rendered under the row
                            currently being split. Purely a manual, reviewed
                            allocation (per Section 24) — never auto-decided. */}
                        {splittingKey === row._key && (
                          <tr key={`${row._key}-split-panel`} className="bg-purple-50/60">
                            <td colSpan={10} className="p-3">
                              <p className="text-xs font-semibold text-purple-700 mb-2">
                                Split "{row.item_name}" ({row.qty} {row.unit}) across multiple locations —
                                each becomes its own stage (e.g. "Floor 1 — {row.stage}").
                              </p>
                              <div className="space-y-1.5">
                                {splitAllocations.map((a, i) => (
                                  <div key={i} className="flex items-center gap-2">
                                    <input value={a.label}
                                      onChange={(e) => updateSplitAllocation(i, "label", e.target.value)}
                                      placeholder="e.g. Floor 1"
                                      className="border rounded px-2 py-1 text-xs w-32" />
                                    <input type="number" value={a.qty}
                                      onChange={(e) => updateSplitAllocation(i, "qty", e.target.value)}
                                      placeholder="Qty"
                                      className="border rounded px-2 py-1 text-xs w-24" />
                                    <span className="text-xs text-gray-400">{row.unit}</span>
                                    {splitAllocations.length > 2 && (
                                      <button type="button" onClick={() => removeSplitAllocation(i)}
                                        className="text-red-400 hover:text-red-600 text-xs">✕</button>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <button type="button" onClick={addSplitAllocation}
                                className="text-xs text-purple-600 font-semibold mt-2">
                                + Add Location
                              </button>

                              {(() => {
                                const allocated = Math.round(
                                  splitAllocations.reduce((s, a) => s + Number(a.qty || 0), 0) * 100
                                ) / 100;
                                const original = Math.round(Number(row.qty || 0) * 100) / 100;
                                const matches = Math.abs(allocated - original) <= 0.01;
                                return (
                                  <p className={`text-xs mt-2 font-semibold ${matches ? "text-green-600" : "text-red-500"}`}>
                                    {matches ? "✓" : "⚠"} Allocated {allocated} / {original} {row.unit}
                                    {!matches && " — must match exactly before applying"}
                                  </p>
                                );
                              })()}

                              <div className="flex gap-2 mt-3">
                                <button type="button" onClick={closeSplit}
                                  className="px-3 py-1.5 rounded-lg border text-xs font-semibold text-gray-600">
                                  Cancel
                                </button>
                                <button type="button" onClick={applySplit}
                                  className="px-3 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-semibold hover:bg-purple-700">
                                  Apply Split →
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-3 mt-5">
                <button onClick={onClose}
                  className="px-4 py-2 rounded-xl border text-sm font-semibold text-gray-600">
                  Cancel
                </button>
                <button onClick={handleConfirm} disabled={step === "saving"}
                  className="px-6 py-2 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50">
                  {step === "saving" ? "Saving..." : "Confirm & Create Stage(s) →"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
